<template>
    <FormLayout>
        <template v-if="value !== null">
            <FormLayoutRow>
                <FormLayoutLabel>{{ $tm('eventinfo.name') }}</FormLayoutLabel>
                <span class="fw-semibold">{{ value.Name }}</span>
            </FormLayoutRow>
            <FormLayoutRow v-if="value instanceof Eventinfo">
                <FormLayoutLabel>{{ $tm('eventinfobase.organizerid') }}</FormLayoutLabel>
                <span class="fw-semibold">{{ value.Organizer?.Name }}</span>
            </FormLayoutRow>
            <FormLayoutRow>
                <FormLayoutLabel>{{ $tm('eventinfo.date') }}</FormLayoutLabel>
                <span class="fw-semibold"
                    ><DateComp
                        v-if="value"
                        :value="value.Date"
                        format="COMLETETEXTDATEYEAR"
                        :monospace="false"
                    ></DateComp
                ></span>
            </FormLayoutRow>
        </template>

        <FormLayoutRow v-if="!hideContestants">
            <FormLayoutLabel>{{ $tm('contestants.contestants') }}</FormLayoutLabel>
            <span
                class="fw-semibold"
                v-if="value !== null && value instanceof Eventinfo"
                >{{ value.NumContestants }}</span
            >
            <span
                class="fw-semibold"
                v-else-if="(contestantcount ?? null) !== null"
                >{{ contestantcount }}</span
            >
            <span
                class="fw-semibold"
                v-else-if="Contestants"
                >{{ Contestants.length }}</span
            >
        </FormLayoutRow>
        <FormLayoutRow v-if="duplicates && duplicates.length > 0">
            <FormLayoutLabel>{{ $tm('contestants.duplicatcontestants') }}</FormLayoutLabel>
            <div class="d-flex flex-row gap-3 align-items-center">
                <span class="fw-semibold">{{ duplicates.length }}</span>
                <ButtonComp
                    buttonType="Link"
                    @click="onShowDuplicates"
                    >{{ $tm('contestants.viewduplicatcontestants') }}</ButtonComp
                >
            </div>
        </FormLayoutRow>

        <FormLayoutRow v-if="races">
            <FormLayoutLabel class="align-self-start">{{ $tm('race.races') }}</FormLayoutLabel>
            <div
                class="d-flex flex-wrap gap-1"
                v-if="props.races"
            >
                <RaceBadges :races="props.races.filter((r) => !raceId || r.ID == raceId)" />
            </div>
        </FormLayoutRow>
        <FormLayoutRow v-if="categories">
            <FormLayoutLabel class="align-self-start">{{ $tm('categories.categories') }}</FormLayoutLabel>
            <div v-if="props.categories">
                <template
                    v-for="([race, cats], i) in groupedCategories"
                    :key="i"
                >
                    <template v-if="!raceId || !race || race.ID == raceId">
                        <div class="row mt-0">
                            <div
                                class="col col-auto"
                                v-if="race"
                            >
                                <span
                                    class="badge bg-light text-dark text-start cursor-help border"
                                    style="width: 66px !important"
                                    :title="race.Name ?? ''"
                                    >{{ getRaceCaption(race) }}</span
                                >
                            </div>
                            <CategoryLabels
                                :cats="cats"
                                class="col"
                            />
                        </div>
                    </template>
                </template>
            </div>
        </FormLayoutRow>

        <FormLayoutRow v-if="props.stationroles">
            <FormLayoutLabel class="align-self-start">{{ $tm('station.stations') }}</FormLayoutLabel>
            <div v-if="props.stationroles">
                <StationRoleBadges
                    :stationroles="stationroles"
                    :raceId="raceId"
                    :races="races"
                />
            </div>
        </FormLayoutRow>
    </FormLayout>
</template>
<script setup lang="ts">
import { ButtonComp, DateComp, RaceBadges, StationRoleBadges } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { $ti } from '@/locize';
import {
    ImportDuplicateContestant,
    Race,
    type Category,
    type CategoryGroupMap,
    Eventinfo,
    type StationRole,
    EventinfoBase,
} from '@/objects/generated';
import { useContestantStoreRef } from '@/stores';
import { computed } from 'vue';
import CategoryLabels from '../Categories/CategoryLabels.vue';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';
import ContestantEventDetailsDuplicates from './ContestantEventDetailsDuplicates.vue';

const props = withDefaults(
    defineProps<{
        value?: Eventinfo | EventinfoBase | null;

        /**
         * A list of categories, if available.
         */
        categories?: Category[] | null;

        /**
         * A list of races
         */
        races?: Race[] | null;

        /**
         * A list of station roles
         */
        stationroles?: StationRole[] | null;

        /**
         * A list of contestants, if available.
         */
        contestantcount?: number | null;

        /**
         * The map that connect the categories to the groups/races
         */
        categoriesGroupConnection?: CategoryGroupMap[] | null;

        /**
         * We only show for one race
         */
        raceId?: number | null;

        /**
         * If we should hide the contestant count
         */
        hideContestants?: boolean;

        /**
         * A list of duplicate contestants that will be ignored
         */
        duplicates?: ImportDuplicateContestant[] | null;
    }>(),
    {
        value: null,
        categories: null,
        races: null,
        stationroles: null,
        contestants: null,
        categoriesGroupConnection: null,
        hideContestants: false,
    },
);

const { Contestants } = useContestantStoreRef();

/**
 * Group and sort the categories based on the races
 */
const groupedCategories = computed(() => {
    let groupMap = new Map<Race | null, Category[]>();
    if (props.categoriesGroupConnection == null || props.races == null) {
        groupMap.set(null, props.categories ?? []);
        return groupMap;
    }

    for (const con of props.categoriesGroupConnection) {
        const cat = props.categories?.find((x) => x.ID == con.CategoryId);
        if (!cat) {
            continue;
        }

        for (const raceId of con.RaceIDs) {
            const race = props.races.find((x) => x.ID == raceId);
            if (race) {
                if (!groupMap.has(race)) {
                    groupMap.set(race, []);
                }
                groupMap.get(race)!.push(cat);

                // Sort the categories on Gender then age
                groupMap.get(race)!.sort((a, b) => {
                    const aGender = a.Gender ?? '';
                    const bGender = b.Gender ?? '';
                    if (aGender !== bGender) {
                        return aGender.localeCompare(bGender);
                    }
                    return (a.AgeFrom ?? 0) - (b.AgeFrom ?? 0);
                });
            }
        }
    }

    // We need to sort again on the Race.Sort property of the groupMap Keys
    groupMap = new Map(
        [...groupMap.entries()].sort((a, b) => {
            const aRace = a[0];
            const bRace = b[0];
            if (aRace && bRace) {
                return aRace.Number - bRace.Number;
            }
            return aRace ? -1 : 1; // If aRace is defined, it comes first
        }),
    );

    return groupMap;
});

const getRaceCaption = (raceId: number | Race | null) => {
    if (raceId == null) {
        return null;
    }

    let race: Race | null = null;
    // Check if raceId is of type number or string
    if (raceId instanceof Race) {
        race = raceId;
    } else {
        race = props.races?.find((x) => x.ID == raceId) ?? null;
    }

    if (!race || !race.Name) {
        return null;
    }

    // If the text is 5 or less long we return all, or else we return the first 5 characters
    return (race.Name?.substring(0, 10) ?? '') + (race.Name!.length > 10 ? '...' : '');
};

/**
 * We show a modal with the duplicate contestants
 */
const onShowDuplicates = async () => {
    const dialogInstance = await useDialogBuilder()
        .withTitle('racecontestantdetails.selectparticipant')
        .withBodyComponent(ContestantEventDetailsDuplicates, {
            componentProps: {
                value: props.duplicates ?? [],
            },
        })
        .withCloseButton({ action: () => true })
        .withOptions({
            Size: 'lg',
            MinHeight: 25,
        })
        .Open();
};
</script>
